import type { ChatMessage } from '../../shared/types.js';
import { t } from '../i18n/i18n.js';
import { clear, el, formatTime, hex } from './dom.js';

/**
 * Saying something, in the lobby and at 120 km/h.
 *
 * One message store, two views of it. The lobby gets a panel that is simply always there,
 * because in a lobby you are waiting and there is nothing else to do. The race gets an
 * overlay that is normally almost invisible: the last few lines, fading out on their own,
 * and an input that only exists while somebody is actually typing.
 *
 * **The part that matters is the keyboard.** Enter opens the race input, and while it is
 * open the driving controls must be *blocked* rather than merely unfocused — otherwise
 * typing "faster" leaves the throttle held down and the brakes on. The game already has
 * exactly that switch for the Escape menu (`setControlsBlocked`), so this reuses it through
 * `onTypingChange` rather than inventing a second way to be not-driving.
 *
 * Text is put on screen with `textContent` throughout, so a message is never markup. The
 * server bounds and strips it as well — see `Lobby.chat` — which is the lock that counts,
 * since it is the one a modified client cannot skip.
 */

/** How many lines hang over the race before the oldest is dropped. */
const RACE_VISIBLE = 5;
/** How long a line stays legible over the road, unless the panel is open. */
const RACE_FADE_MS = 11_000;

export interface ChatOptions {
  onSend(text: string): void;
  /** True while the race input has the keyboard, so the caller can stop the car listening. */
  onTypingChange(typing: boolean): void;
}

export class Chat {
  /** Docked in the lobby, always visible. */
  readonly lobbyRoot: HTMLElement;
  /** Floating over the race, mostly empty. */
  readonly raceRoot: HTMLElement;

  private readonly lobbyLog: HTMLElement;
  private readonly raceLog: HTMLElement;
  private readonly lobbyInput: HTMLInputElement;
  private readonly raceInput: HTMLInputElement;
  private readonly raceForm: HTMLElement;
  private messages: ChatMessage[] = [];
  private fadeTimer: number | null = null;

  constructor(private readonly options: ChatOptions) {
    this.lobbyLog = el('div', { class: 'chat-log', id: 'chat-log' });
    this.lobbyInput = this.input('chat-input', t('Say something'));
    const lobbyForm = this.form(this.lobbyInput);

    this.lobbyRoot = el(
      'section',
      { class: 'chat-panel' },
      el('h3', { class: 'panel-title' }, t('Chat')),
      this.lobbyLog,
      lobbyForm,
    );

    this.raceLog = el('div', { class: 'chat-race-log', id: 'chat-race-log' });
    this.raceInput = this.input('chat-race-input', t('Message… (Enter to send, Esc to cancel)'));
    this.raceForm = this.form(this.raceInput);
    this.raceForm.classList.add('is-hidden');
    this.raceRoot = el('div', { class: 'chat-race' }, this.raceLog, this.raceForm);
  }

  private input(id: string, placeholder: string): HTMLInputElement {
    return el('input', {
      class: 'field chat-field',
      type: 'text',
      id,
      // The server's own cap, so the box stops taking characters at exactly the point they
      // would have been thrown away.
      maxLength: 180,
      placeholder,
      autocomplete: 'off',
    });
  }

  private form(input: HTMLInputElement): HTMLElement {
    const send = el('button', { class: 'button chat-send', type: 'submit' }, t('Send'));
    const form = el('form', { class: 'chat-form' }, input, send);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const text = input.value.trim();
      input.value = '';
      if (text) this.options.onSend(text);
      if (input === this.raceInput) this.closeRaceInput();
    });
    return form;
  }

  // --- messages ------------------------------------------------------------

  /** Replaces everything — the history a joiner is handed. */
  replace(messages: ChatMessage[]): void {
    this.messages = [...messages];
    clear(this.lobbyLog);
    clear(this.raceLog);
    for (const message of this.messages) this.render(message);
    this.scroll();
    this.armFade();
  }

  push(message: ChatMessage): void {
    this.messages.push(message);
    this.render(message);
    // The lobby keeps the scrollback; the race overlay only ever shows the last few.
    while (this.raceLog.childElementCount > RACE_VISIBLE) this.raceLog.firstElementChild?.remove();
    this.scroll();
    this.armFade();
  }

  private render(message: ChatMessage): void {
    const swatch = () => el('span', { class: 'chat-who', style: { color: hex(message.color) } }, message.name);
    // The server's own lines carry no text, only what happened; they are said here, in the
    // reader's language, and set apart from what people typed.
    const finish = message.finish;
    const text = finish
      ? finish.timeMs !== null
        ? t('crossed the finish line · {time}', { time: formatTime(finish.timeMs) })
        : t('crossed the finish line')
      : message.text;
    const line = () =>
      el('p', { class: finish ? 'chat-line chat-event' : 'chat-line' }, swatch(), el('span', { class: 'chat-text' }, text));
    this.lobbyLog.append(line());
    this.raceLog.append(line());
  }

  private scroll(): void {
    this.lobbyLog.scrollTop = this.lobbyLog.scrollHeight;
  }

  /**
   * Fades the race overlay out again after a while.
   *
   * One timer for the whole log rather than one per line: the lines are only interesting as
   * a group, and a dozen pending timeouts during a busy race is a dozen chances to leak one.
   * While the input is open nothing fades, because somebody replying is somebody reading.
   */
  private armFade(): void {
    if (this.fadeTimer !== null) window.clearTimeout(this.fadeTimer);
    this.raceLog.classList.remove('is-faded');
    this.fadeTimer = window.setTimeout(() => {
      this.fadeTimer = null;
      if (!this.isTyping) this.raceLog.classList.add('is-faded');
    }, RACE_FADE_MS);
  }

  // --- the race input ------------------------------------------------------

  get isTyping(): boolean {
    return !this.raceForm.classList.contains('is-hidden');
  }

  openRaceInput(): void {
    if (this.isTyping) return;
    this.raceForm.classList.remove('is-hidden');
    this.raceLog.classList.remove('is-faded');
    this.raceInput.focus();
    this.options.onTypingChange(true);
  }

  closeRaceInput(): void {
    if (!this.isTyping) return;
    this.raceForm.classList.add('is-hidden');
    this.raceInput.value = '';
    this.raceInput.blur();
    this.options.onTypingChange(false);
    this.armFade();
  }

  /** Leaving a race with the box open would strand the controls in the blocked state. */
  reset(): void {
    this.closeRaceInput();
  }

  focusLobbyInput(): void {
    this.lobbyInput.focus();
  }
}
